import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { useNavigate } from "react-router-dom";
import { getFeaturedMovies } from "@/utils/movie";
import { heroBackdrops } from "@/mocks/hero";

export default function HeroCarousel() {
  const navigate = useNavigate();
  const featured = getFeaturedMovies(5);
  const slides = featured.map((movie) => ({
    movie,
    backdrop:
      heroBackdrops.find((item) => item.id === movie.id)?.backdrop ||
      movie.poster,
  }));

  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const total = slides.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % total) + total) % total);
    },
    [total]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5000);
    return () => clearInterval(timer);
  }, [total]);

  const handleTouchStart = (e: TouchEvent) => {
    touchStart.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (touchStart.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStart.current = null;
  };

  const active = slides[index];

  return (
    <section
      className="relative w-full overflow-hidden bg-primary-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="精选热门电影"
    >
      <div className="relative h-[420px] w-full md:h-[560px]">
        {slides.map((slide, i) => (
          <div
            key={slide.movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              i === index ? "opacity-100" : "pointer-events-none opacity-0"
            }`}
          >
            <img
              src={slide.backdrop}
              alt={`${slide.movie.title} 精选海报`}
              title={`${slide.movie.title} 免费在线观看`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent" />
          </div>
        ))}

        <div className="absolute inset-0">
          <div className="mx-auto flex h-full w-full max-w-[1280px] items-center px-4 md:px-6">
            <div key={active.movie.id} className="w-full max-w-xl animate-fade-up">
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-primary-950">
                  <i className="ri-fire-fill" />
                  本周热门
                </span>
                {active.movie.genres.slice(0, 3).map((g) => (
                  <span
                    key={g}
                    className="rounded-full border border-background-300/40 px-3 py-1 text-xs text-background-100"
                  >
                    {g}
                  </span>
                ))}
              </div>
              <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight text-background-50 md:text-5xl">
                {active.movie.title}
              </h1>
              <p className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-background-300">
                <span className="flex items-center gap-1 font-semibold text-accent-400">
                  <i className="ri-star-fill" />
                  {active.movie.rating.toFixed(1)}
                </span>
                <span>{active.movie.year}</span>
                <span>{active.movie.region}</span>
                <span>{active.movie.duration} 分钟</span>
              </p>
              <p className="mt-4 line-clamp-2 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
                {active.movie.synopsis}
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate(`/detail/${active.movie.id}`)}
                  className="flex items-center gap-2 rounded-md bg-accent-500 px-5 py-3 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg" />
                  立即观看
                </button>
                <button
                  type="button"
                  onClick={() => navigate(`/detail/${active.movie.id}`)}
                  className="flex items-center gap-2 rounded-md border border-background-100/40 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-information-line text-lg" />
                  影片详情
                </button>
              </div>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-xl text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 cursor-pointer md:left-6"
        >
          <i className="ri-arrow-left-s-line" />
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-xl text-background-50 backdrop-blur transition-colors hover:bg-background-50/35 cursor-pointer md:right-6"
        >
          <i className="ri-arrow-right-s-line" />
        </button>

        <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
          {slides.map((slide, i) => (
            <button
              key={slide.movie.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${
                i === index ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/50"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}